import { AlarmOn, HourglassFull } from '@mui/icons-material';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import {
  Alert,
  Box,
  Chip,
  IconButton,
  LinearProgress,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import { useCallback, useEffect, useState } from 'react';
import { IDocumentDownloaded } from '../../Interfaces/IDocumentDownloaded';
import { ILegalClient } from '../../Interfaces/ILegalClient';
import {
  ILegalRequirement,
  ILegalRequirementFullObject,
} from '../../Interfaces/ILegalRequirement';
import { ILegalRequirementDeadline } from '../../Interfaces/ILegalRequirement_deadlines';
import { ILegalRequirementState } from '../../Interfaces/ILegalRequirementState';
import { LegalInfoText } from './LegalInfoText';
import { LegalRequirementDocuments } from './LegalRequirementDocuments';

interface IProps {
  legalRequirement: ILegalRequirementFullObject;
  legalRequirementArray: ILegalRequirement[];
  setLegalRequirementArray: Function;
  isNotDisabled: boolean;
  legalReqState: ILegalRequirementState[];
  downloadedArray?: IDocumentDownloaded[];
  /** Mandanten des Vorgangs — ohne sie bleibt die Spalte "Mandant" in
   *  der Download-Uebersicht leer (QS #16). */
  legalClients?: ILegalClient[];
  startDeadlineDate: Date | undefined;
}

export const LegalRequirementRow: React.FC<IProps> = ({
  legalReqState,
  legalRequirement,
  setLegalRequirementArray,
  isNotDisabled,
  legalRequirementArray,
  downloadedArray,
  legalClients,
  startDeadlineDate,
}) => {
  const setLegalRequirement = (updatedLegalReq: ILegalRequirement) => {
    setLegalRequirementArray([
      ...legalRequirementArray.map(x =>
        x.idLegalRequirement === updatedLegalReq.idLegalRequirement
          ? updatedLegalReq
          : x
      ),
    ]);
  };

  const handleChangeValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLegalRequirement({
      ...legalRequirement,
      idLegalRequirementState: Number(event.target.value),
    });
  };

  const addDaysToDate = (date: Date, days: number): Date => {
    const millisecondsPerDay = 1000 * 60 * 60 * 24; // Anzahl der Millisekunden in einem Tag
    const result = new Date(date.getTime() + days * millisecondsPerDay);
    return result;
  };

  const getEarliestTriggerDate = useCallback(
    (deadlineArray: ILegalRequirementDeadline[]) => {
      const highestWeighting = deadlineArray
        .filter(
          deadline =>
            deadline.DeadlineTriggeredAt !== null &&
            deadline.DeadlineTriggeredAt !== undefined
        )
        .reduce((maxWeighting, current) => {
          return current.Weighting > maxWeighting
            ? current.Weighting
            : maxWeighting;
        }, 0);

      const earliestTriggerDate = deadlineArray
        .filter(deadline => {
          return (
            deadline.DeadlineTriggeredAt !== null &&
            deadline.DeadlineTriggeredAt !== undefined
          );
        }) //get all with the highest weighting
        .filter(x => x.Weighting === highestWeighting)
        .reduce((earliestTriggerDate, current) => {
          return new Date(current.DeadlineTriggeredAt!) < earliestTriggerDate
            ? new Date(current.DeadlineTriggeredAt!)
            : earliestTriggerDate;
        }, new Date());

      return earliestTriggerDate;
    },
    []
  );

  const getMaxExceededDays = useCallback(() => {
    const { DeadlineArray, initialProcessingTime } = legalRequirement;

    if (DeadlineArray && DeadlineArray.length > 0) {
      const earliestTriggerDate = getEarliestTriggerDate(DeadlineArray);

      const differenceInTime =
        new Date().getTime() -
        addDaysToDate(
          earliestTriggerDate,
          legalRequirement.initialProcessingTime || 0
        ).getTime();
      return Math.floor(differenceInTime / (1000 * 3600 * 24));
    } else if (startDeadlineDate) {
      // Prüfe, ob das startDate ein gültiges Datum ist
      const startDateObject = new Date(startDeadlineDate);
      if (isNaN(startDateObject.getTime())) {
        return legalRequirement.initialProcessingTime || 0;
      }

      // Hier wird initialProcessingTime korrekt zum Datum addiert
      const adjustedStartDate = addDaysToDate(
        startDateObject,
        initialProcessingTime || 0
      );

      const currentDate = new Date();
      const differenceInTime =
        currentDate.getTime() - adjustedStartDate.getTime();
      return Math.floor(differenceInTime / (1000 * 3600 * 24)); // Kein Math.max, damit negative Werte berücksichtigt werden
    }

    return 0;
  }, [getEarliestTriggerDate, legalRequirement, startDeadlineDate]);

  const hasAtLeastOneDeadlineTriggered = useCallback(() => {
    const { DeadlineArray } = legalRequirement;
    if (DeadlineArray && DeadlineArray.length > 0) {
      return DeadlineArray.some(deadline => {
        if (deadline.Startdate !== null && deadline.Startdate !== undefined) {
          if (new Date(deadline.Startdate) <= new Date()) {
            deadline.DeadlineTriggeredAt = deadline.Startdate;
            return true;
          }
        }
        return (
          deadline.DeadlineTriggeredAt !== null &&
          deadline.DeadlineTriggeredAt !== undefined
        );
      });
    }
    if (startDeadlineDate !== null && startDeadlineDate !== undefined) {
      if (new Date(startDeadlineDate) <= new Date()) {
        return true;
      }
    }
    return false;
  }, [legalRequirement, startDeadlineDate]);

  const getDaysAfterDeadlineForFinishedRequirement = useCallback(() => {
    if (legalRequirement.idLegalRequirementState >= 15) {
      if (
        legalRequirement.DeadlineArray &&
        legalRequirement.DeadlineArray.length > 0
      ) {
        let deadlineFullfilledDate = new Date();

        if (legalRequirement.DeadlineFullfilledAt) {
          deadlineFullfilledDate = new Date(
            legalRequirement.DeadlineFullfilledAt
          );
        }
        const earliestTriggerDate = addDaysToDate(
          getEarliestTriggerDate(legalRequirement.DeadlineArray),
          legalRequirement.initialProcessingTime || 0
        );

        const differenceInTime =
          deadlineFullfilledDate.getTime() - earliestTriggerDate.getTime();
        return Math.floor(differenceInTime / (1000 * 3600 * 24));
      } else {
        let deadlineFullfilledDate = new Date();

        if (legalRequirement.DeadlineFullfilledAt) {
          deadlineFullfilledDate = new Date(
            legalRequirement.DeadlineFullfilledAt
          );
        }
        const daysAfter = addDaysToDate(
          startDeadlineDate ? startDeadlineDate : new Date(),
          legalRequirement.initialProcessingTime || 0
        );
        const differenceInTime =
          deadlineFullfilledDate.getTime() - daysAfter.getTime();
        return Math.floor(differenceInTime / (1000 * 3600 * 24));
      }
    }
    return 0;
  }, [legalRequirement, startDeadlineDate, getEarliestTriggerDate]);

  const [exceededDays, setExceededDays] = useState(getMaxExceededDays());
  const [atLeastOneDeadlineTriggered, setAtLeastOneDeadlineTriggered] =
    useState(hasAtLeastOneDeadlineTriggered());

  const [daysAfterDeadlineForFinish, setDaysAfterDeadlineForFinish] = useState(
    getDaysAfterDeadlineForFinishedRequirement()
  );

  useEffect(() => {
    setExceededDays(getMaxExceededDays());
    setAtLeastOneDeadlineTriggered(hasAtLeastOneDeadlineTriggered());
    setDaysAfterDeadlineForFinish(getDaysAfterDeadlineForFinishedRequirement());
  }, [
    getDaysAfterDeadlineForFinishedRequirement,
    getMaxExceededDays,
    hasAtLeastOneDeadlineTriggered,
    legalRequirement,
    startDeadlineDate,
  ]);

  return (
    <>
      <Alert
        variant={isNotDisabled ? 'standard' : 'outlined'}
        severity={
          legalRequirement.idLegalRequirementState === 40
            ? 'success'
            : legalRequirement.enableError &&
                legalRequirement.idLegalRequirementState === 30
              ? 'error'
              : legalRequirement.idLegalRequirementState === 15 ||
                  legalRequirement.idLegalRequirementState === 20
                ? 'warning'
                : 'info'
        }
        action={
          <>
            <LegalInfoText currentObject={legalRequirement} />
            {isNotDisabled ? (
              <>
                {legalRequirement.isInterneLegalRequirement &&
                  legalRequirement.isEmailSent !== true &&
                  !(
                    legalRequirement.sendEmailAgain &&
                    legalRequirement.sendEmailAgain === true
                  ) &&
                  legalRequirement.EmailBody &&
                  legalRequirement.EmailSubject && (
                    <TextField
                      label='E-Mail senden'
                      size='small'
                      value={
                        legalRequirement.sendInternLegalRequirementMail
                          ? 'Ja'
                          : 'Nein'
                      }
                      onChange={event =>
                        setLegalRequirement({
                          ...legalRequirement,
                          sendInternLegalRequirementMail:
                            event.target.value === 'Ja',
                        })
                      }
                      sx={{ width: 125, mr: 2 }}
                      select
                    >
                      <MenuItem value='Ja'>Ja</MenuItem>
                      <MenuItem value='Nein'>Nein</MenuItem>
                    </TextField>
                  )}
                {(legalRequirement.isEmailSent === true ||
                  legalRequirement.sendEmailAgain === true) && (
                  <TextField
                    label='E-Mail gesendet'
                    size='small'
                    value={legalRequirement.isEmailSent ? 'Ja' : 'Nein'}
                    onChange={event =>
                      setLegalRequirement({
                        ...legalRequirement,
                        isEmailSent: event.target.value === 'Ja',
                        sendEmailAgain: event.target.value === 'Nein',
                      })
                    }
                    sx={{ width: 125, mr: 2 }}
                    select
                  >
                    <MenuItem value='Ja'>Ja</MenuItem>
                    <MenuItem value='Nein'>erneut senden</MenuItem>
                  </TextField>
                )}

                <TextField
                  label='Status'
                  size='small'
                  value={legalRequirement.idLegalRequirementState}
                  onChange={handleChangeValue}
                  sx={{ width: 150 }}
                  select
                >
                  {(legalRequirement.hasError
                    ? legalReqState
                    : legalReqState.filter(
                        x => x.idLegalRequirementState !== 30
                      )
                  ).map(x => (
                    <MenuItem
                      key={x.idLegalRequirementState}
                      value={x.idLegalRequirementState}
                    >
                      {x.LegalRequirementState}
                    </MenuItem>
                  ))}
                </TextField>
              </>
            ) : (
              <i style={{ marginTop: 2 }}>Keine Änderungen möglich</i>
            )}
          </>
        }
        sx={{
          width: '95%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexDirection: 'row',
        }}
      >
        <Box
          width={'100%'}
          display={'flex'}
          alignItems={'center'}
          flexWrap='wrap'
          gap={1}
        >
          {legalRequirement.LegalRequirement}
          {legalRequirement.resolvedCourt?.Court && (
            <Tooltip
              title={`Zuständiges Gericht laut Gemeinde/Gemarkung${legalRequirement.resolvedCourt.fromPhase ? ' (aus Phase)' : ''}: ${legalRequirement.CourtRole ? legalRequirement.CourtRole + ' · ' : ''}${legalRequirement.resolvedCourt.Court}`}
            >
              <Chip
                size='small'
                icon={<GavelIcon />}
                variant='outlined'
                label={legalRequirement.resolvedCourt.Court}
                sx={{ ml: 1 }}
              />
            </Tooltip>
          )}
          {!legalRequirement.resolvedCourt?.Court &&
            legalRequirement.idCourtRole &&
            (() => {
              const reason = legalRequirement.resolvedCourt?.reason;
              const tooltip =
                reason === 'no-admin-unit'
                  ? 'Bitte zuerst die Gemeinde/Gemarkung im Vorgang setzen, damit das zuständige Gericht abgeleitet werden kann.'
                  : reason === 'no-assignment'
                    ? 'Die hinterlegte Gemeinde/Gemarkung hat für diese Rolle kein zugeordnetes Gericht. Bitte in der Ämterverwaltung pflegen.'
                    : 'Zuständiges Gericht konnte nicht abgeleitet werden. Backend ggf. neu starten oder Konsole prüfen.';
              const label =
                reason === 'no-admin-unit'
                  ? `${legalRequirement.CourtRole || 'Rolle'} – Gemarkung fehlt`
                  : `${legalRequirement.CourtRole || 'Rolle'} – kein Gericht zugeordnet`;
              return (
                <Tooltip title={tooltip}>
                  <Chip
                    size='small'
                    icon={<GavelIcon />}
                    variant='outlined'
                    color='warning'
                    label={label}
                    sx={{ ml: 1 }}
                  />
                </Tooltip>
              );
            })()}
          {exceededDays > 0 &&
            legalRequirement.idLegalRequirementState < 15 && (
              <>
                <Tooltip
                  title={`${
                    legalRequirement.Court !== null &&
                    legalRequirement.Court !== undefined
                      ? `Die Maßnahme liegt bei ${legalRequirement.CourtRole} ${legalRequirement.Court}.`
                      : ''
                  } Die Frist dieser Maßnahme wurde um ${exceededDays !== 1 ? `${exceededDays} Tage überschritten` : `${exceededDays} Tag überschritten`}`}
                >
                  <Box
                    display='flex'
                    alignItems='center'
                    sx={{ width: 150 }}
                    ml={4}
                    mr={2}
                  >
                    <LinearProgress
                      variant='determinate'
                      color='error'
                      value={100}
                      sx={{ flexGrow: 1, marginRight: 2 }} // Responsive width with flexGrow
                    />
                  </Box>
                </Tooltip>
              </>
            )}
          {exceededDays <= 0 &&
            atLeastOneDeadlineTriggered &&
            legalRequirement.idLegalRequirementState < 15 && (
              <Tooltip
                title={`${
                  legalRequirement.Court !== null &&
                  legalRequirement.Court !== undefined
                    ? `Die Maßnahme liegt bei ${legalRequirement.CourtRole} ${legalRequirement.Court}.`
                    : ''
                } Die Frist dieser Maßnahme wurde noch nicht überschritten. ${Math.abs(exceededDays) !== 1 ? `Es sind noch ${Math.abs(exceededDays)} Tage übrig` : `Es ist noch ${Math.abs(exceededDays)} Tag übrig`}`}
              >
                <Box
                  display='flex'
                  alignItems='center'
                  sx={{ width: 150 }}
                  ml={4}
                  mr={2}
                >
                  {' '}
                  {Math.abs(exceededDays) <= 5 && (
                    <LinearProgress
                      variant='determinate'
                      color='warning'
                      value={
                        (Math.abs(
                          legalRequirement.initialProcessingTime + exceededDays
                        ) /
                          legalRequirement.initialProcessingTime) *
                        100
                      }
                      sx={{ flexGrow: 1, marginRight: 2 }} // Responsive width with flexGrow
                    />
                  )}
                  {Math.abs(exceededDays) > 5 && (
                    <LinearProgress
                      variant='determinate'
                      color='success'
                      value={
                        (Math.abs(
                          legalRequirement.initialProcessingTime + exceededDays
                        ) /
                          legalRequirement.initialProcessingTime) *
                        100
                      }
                      sx={{ flexGrow: 1, marginRight: 2 }} // Responsive width with flexGrow
                    />
                  )}
                </Box>
              </Tooltip>
            )}
          {!atLeastOneDeadlineTriggered &&
            legalRequirement.idLegalRequirementState < 15 && (
              <Tooltip title='Die Frist dieser Maßnahme wurde noch nicht ausgelöst'>
                <IconButton>
                  <HourglassFull color='disabled'></HourglassFull>
                </IconButton>
              </Tooltip>
            )}
          {legalRequirement.idLegalRequirementState >= 15 &&
            daysAfterDeadlineForFinish <= 0 && (
              <Tooltip
                title={
                  'Die Maßnahme wurde ' +
                  Math.abs(daysAfterDeadlineForFinish) +
                  ' Tage vor Ende der Frist abgeschlossen. ' +
                  (legalRequirement.actualProcessingTime !== null &&
                  legalRequirement.actualProcessingTime !== undefined
                    ? legalRequirement.actualProcessingTime !== 1
                      ? 'Die Bearbeitungszeit betrug ' +
                        legalRequirement.actualProcessingTime +
                        ' Tage'
                      : 'Die Bearbeitungszeit betrug ' +
                        legalRequirement.actualProcessingTime +
                        ' Tag'
                    : 'Der Vorgang wurde noch nicht gespeichert. Aus diesem Grund wurde die Bearbeitungszeit noch nicht berechnet.')
                }
              >
                <IconButton>
                  <AlarmOn color='success'></AlarmOn>
                </IconButton>
              </Tooltip>
            )}

          {legalRequirement.idLegalRequirementState >= 15 &&
            daysAfterDeadlineForFinish > 0 && (
              <Tooltip
                title={
                  `Die Maßnahme wurde ${daysAfterDeadlineForFinish !== 1 ? `${daysAfterDeadlineForFinish} Tage nach der Frist abgeschlossen` : `${daysAfterDeadlineForFinish} Tag nach der Frist abgeschlossen`}. Die Bearbeitungszeit für die Maßnahme betrug: ` +
                  (legalRequirement.actualProcessingTime !== null &&
                  legalRequirement.actualProcessingTime !== undefined
                    ? legalRequirement.actualProcessingTime !== 1
                      ? legalRequirement.actualProcessingTime + ' Tage'
                      : legalRequirement.actualProcessingTime + ' Tag'
                    : legalRequirement.initialProcessingTime !== 1
                      ? legalRequirement.initialProcessingTime + ' Tage'
                      : legalRequirement.initialProcessingTime + ' Tag')
                }
              >
                <IconButton>
                  <AlarmOn color='error'></AlarmOn>
                </IconButton>
              </Tooltip>
            )}
          <LegalRequirementDocuments
            legalRequirementObject={legalRequirement}
            setLegalRequirementObject={setLegalRequirement}
            isNotDisabled={isNotDisabled}
            downloadedArray={downloadedArray}
            legalClients={legalClients}
          />
          {legalRequirement.isInterneLegalRequirement && (
            <Tooltip title='Diese Maßnahme ist intern und nicht öffentlich.'>
              <IconButton>
                <LockIcon />
              </IconButton>
            </Tooltip>
          )}
          {legalRequirement.sendMail === true &&
            legalRequirement.isEmailSent !== true &&
            legalRequirement.sendEmailAgain !== true &&
            legalRequirement.EmailSubject !== '' &&
            legalRequirement.EmailSubject && (
              <Tooltip
                title={'E-Mail wird bei Erledigung der Maßnahme gesendet'}
              >
                <IconButton>
                  <EmailIcon />
                </IconButton>
              </Tooltip>
            )}
        </Box>
      </Alert>
    </>
  );
};
